import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  // Base — solid look, smooth interaction, tactile click feedback
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-semibold transition-[transform,box-shadow,background-color,color,border-color] duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-95 active:shadow-inner select-none",
  {
    variants: {
      variant: {
        // PRIMARY — solid indigo with hover darken + lift, click presses in
        default:
          "bg-indigo-600 text-white shadow-md shadow-indigo-500/30 hover:bg-indigo-700 hover:shadow-lg hover:shadow-indigo-600/40 hover:-translate-y-0.5 active:bg-indigo-800 active:translate-y-0 active:shadow-sm",
        // DESTRUCTIVE — solid rose
        destructive:
          "bg-rose-600 text-white shadow-md shadow-rose-500/30 hover:bg-rose-700 hover:shadow-lg hover:shadow-rose-600/40 hover:-translate-y-0.5 active:bg-rose-800 active:translate-y-0 active:shadow-sm",
        // OUTLINE — solid white with indigo border emerging on hover
        outline:
          "bg-white text-slate-700 border border-slate-300 shadow-sm hover:bg-indigo-50 hover:border-indigo-500 hover:text-indigo-700 hover:-translate-y-0.5 hover:shadow-md active:bg-indigo-100 active:translate-y-0 active:shadow-sm",
        // SECONDARY — solid slate
        secondary:
          "bg-slate-100 text-slate-900 border border-slate-200 shadow-sm hover:bg-slate-200 hover:border-slate-300 hover:-translate-y-0.5 hover:shadow-md active:bg-slate-300 active:translate-y-0 active:shadow-sm",
        // GHOST — transparent base, solid bg on hover
        ghost:
          "text-slate-700 hover:bg-indigo-100 hover:text-indigo-700 active:bg-indigo-200",
        // LINK — text only
        link:
          "text-indigo-700 underline-offset-4 hover:underline hover:text-indigo-900 active:text-indigo-950",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-lg px-6",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
